import { Avatar, Badge, Button, Divider, Flex, Group, Modal, Table, Text, TextInput } from "@mantine/core";
import { IconEdit, IconPlus, IconServer, IconTrash } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { TenantApi } from "../../apis";
import { PageLayout } from "../../components/layout";
import { Message } from "../../utils/message";
import { resolveAdminError } from "../../utils/admin-errors";

const useBreadcrumb = () => [
  {
    to: "/",
    label: "Эхлэл",
  },
  {
    label: "Tenant жагсаалт",
  },
];

const TenantList = () => {
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumb();
  const { data: tenants, mutate } = useSWR("swr.tenants.list", () => TenantApi.list());
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState<{ name?: string; domain?: string; adminEmail?: string; phone?: string }>({});
  const [creating, setCreating] = useState(false);

  const resetForm = useCallback(() => {
    setName("");
    setDomain("");
    setAdminEmail("");
    setPhone("");
    setErrors({});
  }, []);

  const handleCloseCreate = useCallback(() => {
    setCreateOpen(false);
    resetForm();
  }, [resetForm]);

  const handleCreate = useCallback(async () => {
    const nextErrors: { name?: string; domain?: string; adminEmail?: string; phone?: string } = {};
    if (!name.trim()) nextErrors.name = "Нэр оруулна уу";
    if (!domain.trim()) nextErrors.domain = "Домэйн оруулна уу";
    if (!adminEmail.trim()) nextErrors.adminEmail = "Админ и-мэйл оруулна уу";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail.trim())) nextErrors.adminEmail = "И-мэйл хаяг буруу байна";
    if (!phone.trim()) nextErrors.phone = "Утасны дугаар оруулна уу";
    else if (!/^[9|8|7|6]{1}[0-9]{7}$/.test(phone.trim())) nextErrors.phone = "Утасны дугаарын формат буруу байна!";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setCreating(true);
    try {
      await TenantApi.create({ name: name.trim(), domain: domain.trim(), adminEmail: adminEmail.trim(), phone: phone.trim() });
      setCreateOpen(false);
      resetForm();
      mutate();
    } catch (err) {
      Message.error(resolveAdminError(err));
    } finally {
      setCreating(false);
    }
  }, [name, domain, adminEmail, phone, mutate, resetForm]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm("Устгахдаа итгэлтэй байна уу?")) return;
      await TenantApi.remove(id);
      mutate();
    },
    [mutate],
  );

  const handleSetLocalhost = useCallback(
    async (id: string) => {
      await TenantApi.setLocalhost(id);
      mutate();
    },
    [mutate],
  );

  const handleUnsetLocalhost = useCallback(
    async (id: string) => {
      await TenantApi.unsetLocalhost(id);
      mutate();
    },
    [mutate],
  );

  return (
    <PageLayout
      title="Tenant удирдлага"
      subTitle="Платформын tenant бүртгэл, брэндинг тохиргоо"
      breadcrumb={breadcrumbs}
      extra={[
        <Button key="create" leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
          Шинэ tenant
        </Button>,
      ]}
    >
      <Divider mb="xl" />
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Лого</Table.Th>
            <Table.Th>Нэр</Table.Th>
            <Table.Th>Домэйн</Table.Th>
            <Table.Th>Төлөв</Table.Th>
            <Table.Th>Үүсгэсэн</Table.Th>
            <Table.Th>Үйлдэл</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(tenants || []).map((t: any) => (
            <Table.Tr key={t.id}>
              <Table.Td>
                <Avatar src={t.logo} radius="sm" size="sm">
                  {t.name?.[0]}
                </Avatar>
              </Table.Td>
              <Table.Td>{t.name}</Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {t.domain}
                </Text>
              </Table.Td>
              <Table.Td>
                <Badge color={t.isActive ? "green" : "red"} variant="light">
                  {t.isActive ? "Идэвхтэй" : "Идэвхгүй"}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {t.createdAt ? new Date(t.createdAt).toLocaleDateString("mn-MN") : "-"}
                </Text>
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  {t.isLocalhost ? (
                    <Button size="xs" variant="light" color="green" leftSection={<IconServer size={14} />} onClick={() => handleUnsetLocalhost(t.id)}>
                      Localhost
                    </Button>
                  ) : (
                    <Button size="xs" variant="outline" color="gray" leftSection={<IconServer size={14} />} onClick={() => handleSetLocalhost(t.id)}>
                      Localhost
                    </Button>
                  )}
                  <Button size="xs" variant="light" leftSection={<IconEdit size={14} />} onClick={() => navigate(`/tenant/${t.id}`)}>
                    Засах
                  </Button>
                  <Button size="xs" variant="light" color="red" leftSection={<IconTrash size={14} />} onClick={() => handleDelete(t.id)}>
                    Устгах
                  </Button>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={createOpen} onClose={handleCloseCreate} title="Шинэ tenant үүсгэх">
        <Flex direction="column" gap="md">
          <TextInput
            label="Нэр"
            placeholder="Компаний нэр"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            error={errors.name}
            withAsterisk
          />
          <TextInput
            label="Домэйн"
            placeholder="b2b.company.mn"
            value={domain}
            onChange={(e) => {
              setDomain(e.target.value);
              if (errors.domain) setErrors((prev) => ({ ...prev, domain: undefined }));
            }}
            error={errors.domain}
            withAsterisk
          />
          <TextInput
            label="Админ и-мэйл"
            placeholder="admin@company.mn"
            type="email"
            value={adminEmail}
            onChange={(e) => {
              setAdminEmail(e.target.value);
              if (errors.adminEmail) setErrors((prev) => ({ ...prev, adminEmail: undefined }));
            }}
            error={errors.adminEmail}
            withAsterisk
          />
          <TextInput
            label="Админы утас"
            placeholder="99112233"
            inputMode="numeric"
            maxLength={8}
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value.replace(/\D/g, ""));
              if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
            }}
            error={errors.phone}
            withAsterisk
          />
          <Button onClick={handleCreate} loading={creating}>
            Үүсгэх
          </Button>
        </Flex>
      </Modal>
    </PageLayout>
  );
};

export default TenantList;
